import React, { useEffect, useContext, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { SettingsContext } from '@/context/settings-context'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { CharLimitedInput } from '@/components/common/CharLimitedInput'
import { Textarea } from '@/components/ui/textarea'
import { LeadFormQuestionsView } from './instantForm/LeadFormQuestionsView'
import { CubeLoader } from '@/components/ui/cube-loader'
import { useFormThankYou } from '../hooks/useFormThankYou'

const Navigation = ({ goBack, handleNext }) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button className="tw:w-[156px] tw:!text-sm" onClick={handleNext}>
                Next
            </Button>
        </div>
    )
}

export const FormThankYou = () => {
    const { goBack, goNext, dataForStepper, registerCustomNavigation, clearCustomNavigation } = useWizard()
    const { theme } = useContext(SettingsContext)

    const {
        thankYouMessage,
        description,
        websiteButtonText,
        websiteUrl,
        handleThankYouMessageChange,
        handleDescriptionChange,
        handleWebsiteButtonTextChange,
        handleWebsiteUrlChange,
        formErrors,
        validateForm,
        submitForm,
        isFormProcessing,
        thankYouQuestion,
        pageIcon,
        pageTitle,
    } = useFormThankYou()

    const goBackRef = useRef(goBack)
    const validateFormRef = useRef(validateForm)
    const submitFormRef = useRef(submitForm)
    const goNextRef = useRef(goNext)

    useEffect(() => {
        goBackRef.current = goBack
        validateFormRef.current = validateForm
        submitFormRef.current = submitForm
        goNextRef.current = goNext
    }, [goBack, validateForm, submitForm, goNext])

    const handleBack = useCallback(() => {
        goBackRef.current()
    }, [])

    const handleNext = useCallback(async () => {
        const isValid = validateFormRef.current(false)
        if (!isValid) return

        const submitted = await submitFormRef.current()
        if (submitted) goNextRef.current()
    }, [])

    useEffect(() => {
        registerCustomNavigation(
            <Navigation goBack={handleBack} handleNext={handleNext} />,
        )

        return () => clearCustomNavigation()
    }, [registerCustomNavigation, clearCustomNavigation, handleBack, handleNext])

    if (isFormProcessing) {
        return (
            <div className="tw:flex tw:h-full">
                <WizardSteps adCreative={dataForStepper} currentStep="formThankYou" />
                <div className={`d-flex h-100 ${theme} campaigns-container w-100 position-relative`}>
                    <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                        <CubeLoader size="default" fullScreen={true} />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="formThankYou" />
            <div className={`d-flex h-100 ${theme} campaigns-container w-100 position-relative`}>
                <div className="border-right-white-1 pt-4 tw:px-4 tw:position-relative">
                    <div className="tw:pb-4 tw:text-sm tw:w-[547px]">
                        Confirm successful submission and let people know what happens next.
                    </div>

                    <div className="mb-4">
                        <label className="form-label tw:text-base">Headline</label>
                        <CharLimitedInput
                            value={thankYouMessage}
                            onChange={handleThankYouMessageChange}
                            placeholder="Thanks, you're all set."
                            maxChars={60}
                            className={formErrors['thankYouMessage'] ? 'tw:!border-red-500' : ''}
                        />
                        {formErrors['thankYouMessage'] && (
                            <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1">
                                {formErrors['thankYouMessage']}
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                        <label className="form-label tw:text-base">Description</label>
                        <div className="tw:pb-4">
                            <Textarea
                                value={description}
                                onChange={handleDescriptionChange}
                                placeholder="You can visit our website or exit from the form now."
                                rows={3}
                                className={formErrors['description'] ? 'tw:!border-red-500' : ''}
                            />
                            {formErrors['description'] && (
                                <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1">
                                    {formErrors['description']}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Website Button */}
                    <div className="mb-4">
                        <label className="form-label tw:text-base">Call to Action</label>
                        <CharLimitedInput
                            value={websiteButtonText}
                            onChange={handleWebsiteButtonTextChange}
                            placeholder="View website"
                            maxChars={30}
                            className={formErrors['websiteButtonText'] ? 'tw:!border-red-500' : ''}
                        />
                        {formErrors['websiteButtonText'] && (
                            <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1">
                                {formErrors['websiteButtonText']}
                            </div>
                        )}
                    </div>

                    {/* Website URL */}
                    <div className="mb-4">
                        <label className="form-label tw:text-base">URL</label>
                        <CharLimitedInput
                            value={websiteUrl}
                            onChange={handleWebsiteUrlChange}
                            placeholder={'https://example.com'}
                            className={formErrors['websiteUrl'] ? 'tw:!border-red-500' : ''}
                        />
                        {formErrors['websiteUrl'] && (
                            <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1">{formErrors['websiteUrl']}</div>
                        )}
                    </div>
                </div>

                {/* Phone Preview */}
                <div className="position-relative tw:p-6" style={{ width: 320, maxWidth: 320 }}>
                    <LeadFormQuestionsView
                        questions={[thankYouQuestion]}
                        pageIcon={pageIcon}
                        pageTitle={pageTitle}
                        theme={theme}
                        hideSelectForm={true}
                    />
                </div>
            </div>
        </div>
    )
}
